import _ from 'lodash';
import AuthApi from '@/store/api/system/auth';

/**
 * Build a token-authenticated thumbnail URL for a product media image item,
 * mirroring the logic in ProductMedium.vue's `src` computed (image branch).
 * This URL is for in-browser display only — the backend reads the original
 * bytes directly when sending images to fal.ai / Mistral.
 */
export function imageThumbnailUrl(mediaItem) {
    const image = _.get(mediaItem, 'image');
    const source = _.get(image, 'source');
    if (!image || !source) {
        return null;
    }

    // Always the ORIGINAL source thumbnail. The picker tile shows the active/chosen
    // variant via frameThumb(); the per-variant picker shows each variant's own
    // pixels — so this must stay the source, not the active layer.
    const storageRoute = _.trim(
        `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`,
        '/'
    );
    const table = source.upload ? 'uploads' : 'images';
    const id = source.upload ? source.id : image.id;
    const token = AuthApi.getToken();

    return `${storageRoute}/${table}/${id}/${source.name}?hash=${source.hash}&token=${token}&fit&h=150`;
}

/**
 * Full-size source URL (no resize) for an image's original — works for both a
 * persisted image (images/{id}) and a freshly-uploaded one whose source lives in
 * the uploads table (uploads/{uploadId}). Used as the cropper canvas src when the
 * image isn't persisted yet (no `source.url`).
 */
export function originalImageUrl(image) {
    if (!image) {
        return null;
    }
    // Three upload shapes exist:
    //  • nested `source` with `source.upload` (product / content / editor uploads) →
    //    pixels live in the uploads table.
    //  • a FLAT shape (the business-card form's own el-upload sets { id, name, upload }
    //    directly on the image, no `source`).
    //  • a LIBRARY selection: a nested `source` (a real, existing File) with NO
    //    `source.upload`, while the image's own id is still an unsaved
    //    `new:file-library:` token. The pixels are an existing File, so an
    //    /images/{id} URL can't be built (the id is a token) — serve from the files
    //    table by `source.id`, which FileController crop-serves like any other File.
    const source = _.get(image, 'source');
    const imageId = _.get(image, 'id');
    const isLibrarySelection = !!source && !source.upload
        && typeof imageId === 'string' && imageId.indexOf('new:file-library:') === 0;

    let table;
    let id;
    let name;
    let hash;
    if (isLibrarySelection) {
        // A library pick can carry a picked VERSION (`processedFile`) — the crop / AI edit
        // the user chose out of the file's stack. Those are the pixels the canvas shows, so
        // they are also the pixels a crop must start from. Serving `source.id` here cropped
        // the UN-EDITED source instead: callers append `base_file_id` to say "crop that
        // layer", but FileController only honours it for Image models, and a library pick
        // has no Image yet — it is served straight off the files table.
        const base = _.get(image, 'processedFile') || source;
        table = 'files';
        id = base.id;
        name = base.name;
        hash = base.hash || '';
    } else {
        const upload = source ? source.upload : _.get(image, 'upload');
        table = upload ? 'uploads' : 'images';
        id = source ? (source.upload ? source.id : image.id) : _.get(image, 'id');
        name = source ? source.name : _.get(image, 'name');
        hash = (source ? source.hash : _.get(image, 'hash')) || '';
    }
    if (!id || !name) {
        return null;
    }
    const storageRoute = _.trim(
        `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`,
        '/'
    );
    const token = AuthApi.getToken();
    return `${storageRoute}/${table}/${id}/${name}?hash=${hash}&token=${token}`;
}

/**
 * Token-authenticated app-proxy URL for a concrete File (variant layers, processed
 * files). The editor must NEVER render a raw CDN `file.url`: on DigitalOcean the CDN
 * URL is built regardless of the object's visibility, so a private file (e.g. a
 * content image of a restricted page) returns AccessDenied — the "broken canvas
 * image" bug. The /storage/private/files/{id}/{name} route authorizes the admin via
 * the token and streams the pixels whatever the object ACL is (the same route the
 * library thumbnails already use). Returns null when id/name are missing — callers
 * fall back to the raw URL then.
 */
export function proxiedFileUrl(file) {
    const id = parseInt(_.get(file, 'id'), 10);
    const name = _.get(file, 'name');
    if (!id || !name) {
        return null;
    }
    const storageRoute = _.trim(
        `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`,
        '/'
    );
    const token = AuthApi.getToken();
    const hash = _.get(file, 'hash') || '';
    return `${storageRoute}/files/${id}/${encodeURIComponent(name)}?hash=${hash}&token=${token}`;
}

/**
 * FileController crop URL for a PERSISTED image's DEFERRED crop (settings.processing).
 * A static source.url CDN file ignores ?fit=crop&…+rot/flip query params — only the
 * /images/{id} route (FileController) applies them. `fitString` is the query built from
 * settings.processing (getFitStringFromProcessObject). Returns null for fresh uploads
 * (no persisted id) or when there's nothing to apply.
 */
export function croppedImageUrl(image, fitString) {
    if (!fitString) {
        return null;
    }
    // Reuse originalImageUrl's table/id/name/hash/token resolution — it correctly handles
    // a persisted image (/images/{id}), a fresh upload (/uploads/{uploadId}) AND a library
    // pick (/files/{fileId}). Append the crop query so FileController applies it.
    const base = originalImageUrl(image);
    if (!base) {
        return null;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}${fitString}`;
}

/**
 * Map a product's media list to the image options the drawers show:
 * { id (image id), thumbnail, name }.
 *
 * Ordered by `position` to match the product Media list (which sorts the same
 * way) — otherwise the drawer would show images in a different order.
 */
export function productImageOptions(product) {
    return _.get(product, 'media', [])
        .filter(item => item.type === 'image' && _.get(item, 'image.id'))
        .slice()
        .sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0))
        .map(item => buildImageOption(item));
}

/**
 * Single-image option for the unified edit drawer in no-product mode (business
 * cards, CMS content). Returns an array (0 or 1 entries) so it can stand in for
 * `productImageOptions` directly.
 */
export function singleImageOption(image) {
    if (!image || !_.get(image, 'id')) {
        return [];
    }
    // Wrap the bare image so imageThumbnailUrl (which reads `mediaItem.image`) works.
    return [buildImageOption({ image })];
}

/**
 * Map one media-like item ({ image }) to the drawer's option shape. Shared by the
 * product (many images) and single-image (one image) paths so the variant/layer
 * logic lives in exactly one place.
 */
function buildImageOption(item) {
    const image = _.get(item, 'image');
    const imageId = parseInt(_.get(image, 'id'), 10);
    const activeToken = _.get(image, 'processing_query_string') || null;
    const sourceFileId = parseInt(_.get(image, 'source.id'), 10) || null;

    // Each frame is a selectable key-frame for the video. We expose the PRISTINE
    // original AND every concrete edit (crop / AI) as distinct frames, so the user
    // can decide which pixels the video is built from. Critical: a crop applied
    // "in place" is folded into the synthesized `original` variant (its `file` is
    // the crop derivative). If we mapped that variant straight through, the tile
    // would show the source thumbnail while silently generating from the crop —
    // and `hasVariants` would be false, hiding the chooser entirely. So we rebuild
    // the frame list explicitly: source first, then real layers, then the active
    // in-place crop derivative as its own "Crop" frame.
    const frames = [];
    const seenFileIds = new Set();

    // 1) Pristine original — the untouched source pixels (proven thumbnail route).
    if (sourceFileId) {
        frames.push({
            fileId: sourceFileId,
            kind: 'original',
            label: null,
            active: !activeToken,
            thumbnail: imageThumbnailUrl(item),
        });
        seenFileIds.add(sourceFileId);
    }

    // 2) Real layers (AI edits, concrete crop layers). Skip the synthesized
    //    `original` entry — its pixels are the source (frame 1) or the active
    //    in-place crop (frame 3), never a separate pick.
    (_.get(image, 'variants') || []).forEach(variant => {
        if (_.get(variant, 'kind') === 'original') {
            return;
        }
        const fileId = parseInt(_.get(variant, 'file.id'), 10) || null;
        if (!fileId || seenFileIds.has(fileId)) {
            return;
        }
        seenFileIds.add(fileId);
        frames.push({
            fileId,
            kind: _.get(variant, 'kind') || 'crop',
            label: _.get(variant, 'label') || null,
            active: _.get(variant, 'file.type') === activeToken,
            thumbnail: _.get(variant, 'file.url') || null,
        });
    });

    // 3) Active in-place crop derivative (folded into `original`, so not a layer):
    //    surface it as its own "Crop" frame the user can pick vs the pristine original.
    const processed = _.get(image, 'processedFile');
    const processedId = parseInt(_.get(processed, 'id'), 10) || null;
    if (activeToken && processedId && !seenFileIds.has(processedId)) {
        seenFileIds.add(processedId);
        frames.push({
            fileId: processedId,
            kind: 'crop',
            label: null,
            active: true,
            thumbnail: _.get(processed, 'url') || null,
        });
    }

    // Fallback when nothing resolved (e.g. fresh upload with no source File id yet):
    // a single original frame off the thumbnail route.
    let resolvedFrames = frames.filter(frame => frame.fileId);
    if (!resolvedFrames.length) {
        resolvedFrames = [{
            fileId: sourceFileId,
            kind: 'original',
            label: null,
            active: true,
            thumbnail: imageThumbnailUrl(item),
        }];
    }
    // Exactly one active frame drives the default selection + tile thumbnail.
    if (!resolvedFrames.some(frame => frame.active)) {
        resolvedFrames[0].active = true;
    }
    const activeFrame = resolvedFrames.find(frame => frame.active) || resolvedFrames[0];

    return {
        // GraphQL returns ids as strings; the mutations expect Int — coerce here.
        id: imageId,
        thumbnail: imageThumbnailUrl(item),
        name: _.get(image, 'title') || _.get(image, 'source.name') || '',
        frames: resolvedFrames,
        activeFileId: activeFrame ? activeFrame.fileId : null,
        hasVariants: resolvedFrames.length > 1,
    };
}
